/**
 * WorkspacesV2Context — workspaces multi-tenancy reales.
 * 
 * NO confundir con WorkspaceContext.js viejo, que en realidad gestiona
 * la MARCA (client) activa, no un workspace real.
 * 
 * Estado:
 * - workspaces: lista de workspaces donde el user es miembro
 * - activeWorkspace: workspace seleccionado actualmente
 * - activeBrand: marca seleccionada dentro del workspace activo
 * - loading, error
 * 
 * Persistencia en localStorage:
 * - 'adpulse_v2_workspace_id' = workspace activo
 * - 'adpulse_v2_brand_id' = marca activa
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { workspacesApi, brandsApi } from '@/components/v2/api';

const WorkspacesV2Context = createContext(null);

const LS_WS_KEY = 'adpulse_v2_workspace_id';
const LS_BRAND_KEY = 'adpulse_v2_brand_id';

export function WorkspacesV2Provider({ children }) {
  const [workspaces, setWorkspaces] = useState([]);
  const [activeWorkspace, setActiveWorkspaceState] = useState(null);
  const [brands, setBrands] = useState([]);
  const [activeBrand, setActiveBrandState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ============ LOAD WORKSPACES (al iniciar y cuando hay token) ============

  const loadWorkspaces = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setWorkspaces([]);
      setActiveWorkspaceState(null);
      setBrands([]);
      setActiveBrandState(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const ws = await workspacesApi.list();
      setWorkspaces(ws);

      if (ws.length === 0) {
        setActiveWorkspaceState(null);
        localStorage.removeItem(LS_WS_KEY);
        setLoading(false);
        return;
      }

      // Restaurar workspace activo desde localStorage si existe y es válido
      const savedId = localStorage.getItem(LS_WS_KEY);
      const saved = savedId ? ws.find(w => w.id === savedId) : null;
      const active = saved || ws[0];
      setActiveWorkspaceState(active);
      localStorage.setItem(LS_WS_KEY, active.id);
    } catch (e) {
      console.error('Error loading workspaces v2:', e);
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // ============ LOAD BRANDS (cuando cambia el workspace activo) ============

  const loadBrands = useCallback(async (workspaceId) => {
    if (!workspaceId) {
      setBrands([]);
      setActiveBrandState(null);
      return;
    }
    try {
      const bs = await brandsApi.list(workspaceId);
      setBrands(bs);

      if (bs.length === 0) {
        setActiveBrandState(null);
        localStorage.removeItem(LS_BRAND_KEY);
        return;
      }

      const savedBrandId = localStorage.getItem(LS_BRAND_KEY);
      const saved = savedBrandId ? bs.find(b => b.id === savedBrandId) : null;
      const active = saved || bs[0];
      setActiveBrandState(active);
      localStorage.setItem(LS_BRAND_KEY, active.id);
    } catch (e) {
      console.error('Error loading brands:', e);
      setError(e?.response?.data?.detail || e.message);
    }
  }, []);

  useEffect(() => {
    loadWorkspaces();
  }, [loadWorkspaces]);

  useEffect(() => {
    if (activeWorkspace) {
      loadBrands(activeWorkspace.id);
    }
  }, [activeWorkspace, loadBrands]);

  // ============ SETTERS (con persistencia) ============

  const setActiveWorkspace = useCallback((ws) => {
    setActiveWorkspaceState(ws);
    if (ws) {
      localStorage.setItem(LS_WS_KEY, ws.id);
      localStorage.removeItem(LS_BRAND_KEY);  // resetear brand al cambiar de workspace
      setActiveBrandState(null);
    } else {
      localStorage.removeItem(LS_WS_KEY);
    }
  }, []);

  const setActiveBrand = useCallback((brand) => {
    setActiveBrandState(brand);
    if (brand) {
      localStorage.setItem(LS_BRAND_KEY, brand.id);
    } else {
      localStorage.removeItem(LS_BRAND_KEY);
    }
  }, []);

  // ============ ACTIONS ============

  const refreshWorkspaces = useCallback(async () => {
    await loadWorkspaces();
  }, [loadWorkspaces]);

  const refreshBrands = useCallback(async () => {
    if (activeWorkspace) {
      await loadBrands(activeWorkspace.id);
    }
  }, [activeWorkspace, loadBrands]);

  const createWorkspace = useCallback(async ({ name, plan_tier = 'free' }) => {
    const newWs = await workspacesApi.create({ name, plan_tier });
    await loadWorkspaces();
    setActiveWorkspace(newWs);
    return newWs;
  }, [loadWorkspaces, setActiveWorkspace]);

  const createBrand = useCallback(async (data) => {
    if (!activeWorkspace) throw new Error('No active workspace');
    const newBrand = await brandsApi.create(activeWorkspace.id, data);
    await loadBrands(activeWorkspace.id);
    setActiveBrand(newBrand);
    return newBrand;
  }, [activeWorkspace, loadBrands, setActiveBrand]);

  const value = {
    // State
    workspaces,
    activeWorkspace,
    brands,
    activeBrand,
    loading,
    error,
    // Setters
    setActiveWorkspace,
    setActiveBrand,
    // Actions
    refreshWorkspaces,
    refreshBrands,
    createWorkspace,
    createBrand,
    // Computed
    myRole: activeWorkspace?.my_role || null,
    isOwner: activeWorkspace?.my_role === 'owner',
    isAdmin: ['owner', 'admin'].includes(activeWorkspace?.my_role),
  };

  return (
    <WorkspacesV2Context.Provider value={value}>
      {children}
    </WorkspacesV2Context.Provider>
  );
}

export function useWorkspacesV2() {
  const ctx = useContext(WorkspacesV2Context);
  if (!ctx) {
    throw new Error('useWorkspacesV2 must be used within WorkspacesV2Provider');
  }
  return ctx;
}
